import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import {
  DatePicker,
  Table,
  Tag,
  Spin,
  Empty,
  Button,
  Alert
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/pro-duotone-svg-icons';
import {
  notificationsAtom,
  amaDashboardCurrentTabAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { Statuses } from './otp/otpCalculations';
import { thousand } from '../../../../functions';

const STATUS_COLORS = {
  [Statuses.OnTime]: '#16a34a',
  [Statuses.Early]: '#f59e0b',
  [Statuses.Delayed]: '#dc2626'
};

function AmaDashboardOtp() {
  const { t, i18n } = useTranslation();
  const currentTab = useRecoilValue(amaDashboardCurrentTabAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const isActiveTab = currentTab === '2';

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [historicalData, setHistoricalData] = useState(null);
  const [loadingHistorical, setLoadingHistorical] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Session-only banner dismissal: the alert reappears whenever the user
  // navigates back to this page so they're reminded the data is estimated.
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(false);

  const isToday = selectedDate && selectedDate.isSame(dayjs(), 'day');

  const pushError = (description) => {
    setNotifications([...notifications, { title: t('error'), description, error: true }]);
  };

  // Keep dayjs locale in sync with the i18n language so MMM month names and
  // any other locale-aware tokens render in the user's selected language.
  useEffect(() => {
    dayjs.locale(i18n.language?.toLowerCase().startsWith('es') ? 'es' : 'en');
  }, [i18n.language]);

  useEffect(() => {
    if (!isActiveTab || !selectedDate) return undefined;
    let cancelled = false;
    setLoadingHistorical(true);
    api.get(`ama/dashboard/otp/historical?date=${selectedDate.format('YYYY-MM-DD')}`)
      .then((res) => { if (!cancelled) setHistoricalData(res.data?.data ?? res.data); })
      .catch((err) => { if (!cancelled) pushError(err.response?.data?.message || err.message); })
      .finally(() => { if (!cancelled) setLoadingHistorical(false); });
    return () => { cancelled = true; };
  }, [isActiveTab, selectedDate, refreshTick]);

  const tableData = useMemo(() => {
    return (historicalData?.routes || []).map((r) => ({
      key: r.routeId,
      routeId: r.routeId,
      routeName: r.routeName || r.routeId,
      assignedVehicles: r.assignedVehicles,
      totalTrips: r.totalTrips,
      onTimeCount: r.onTimeCount,
      earlyCount: r.earlyCount,
      delayedCount: r.delayedCount,
      otpPercentage: r.otpPercentage,
      trips: r.trips
    }));
  }, [historicalData]);

  const overallStats = useMemo(() => {
    const total = tableData.reduce((s, r) => s + (r.totalTrips || 0), 0);
    const onTime = tableData.reduce((s, r) => s + (r.onTimeCount || 0), 0);
    const late = tableData.reduce((s, r) => s + (r.delayedCount || 0), 0);
    const early = tableData.reduce((s, r) => s + (r.earlyCount || 0), 0);
    const pct = (n) => (total === 0 ? 0 : Math.round((n / total) * 100));
    return {
      total,
      onTimeCount: onTime,
      lateCount: late,
      earlyCount: early,
      onTimePercent: pct(onTime),
      latePercent: pct(late),
      earlyPercent: pct(early)
    };
  }, [tableData]);

  const renderStatusTag = (status) => {
    const color = status === Statuses.OnTime
      ? 'green'
      : status === Statuses.Early
        ? 'blue'
        : status === Statuses.Delayed
          ? 'red'
          : 'default';
    const label = t(status === Statuses.OnTime ? 'on_time' : status);
    return <Tag color={color}>{label}</Tag>;
  };

  const tableColumns = [
    { title: t('route'), dataIndex: 'routeName', key: 'routeName' },
    {
      title: t('assigned_vehicle'),
      dataIndex: 'assignedVehicles',
      key: 'assignedVehicles',
      render: (vehiclesList) => (vehiclesList && vehiclesList.length > 0)
        ? vehiclesList.join(', ')
        : <span className="text-gray-400">—</span>
    },
    {
      title: t('total_trips'),
      dataIndex: 'totalTrips',
      key: 'totalTrips',
      width: 110,
      render: (count) => thousand(count)
    },
    { title: t('on_time'), dataIndex: 'onTimeCount', key: 'onTimeCount', width: 100 },
    { title: t('delayed'), dataIndex: 'delayedCount', key: 'delayedCount', width: 100 },
    {
      title: t('otp_percentage'),
      dataIndex: 'otpPercentage',
      key: 'otpPercentage',
      width: 110,
      render: (pct) => `${pct}%`
    }
  ];

  const formatTimeOfDay = (iso) => (iso ? dayjs(iso).format('hh:mm A') : '—');

  const expandedRowRender = (row) => {
    const tripColumns = [
      { title: t('stop'), dataIndex: 'terminalName', key: 'terminalName' },
      { title: t('bus'), dataIndex: 'vehicleName', key: 'vehicleName', width: 110 },
      {
        title: t('scheduled_time'),
        dataIndex: 'scheduledDeparture',
        key: 'scheduledDeparture',
        width: 110,
        render: formatTimeOfDay
      },
      {
        title: t('actual_arrival'),
        dataIndex: 'actualArrival',
        key: 'actualArrival',
        width: 110,
        render: formatTimeOfDay
      },
      {
        title: t('status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: renderStatusTag
      },
      {
        title: t('delay_minutes'),
        dataIndex: 'delayMinutes',
        key: 'delayMinutes',
        width: 110,
        render: (m) => (m === null || m === undefined ? '—' : `${m}`)
      }
    ];
    // Stops appear in the order they were scheduled to be visited that day,
    // so the user can read down the route's timeline.
    const orderedTrips = [...(row.trips || [])].sort((a, b) => {
      const aTs = a.scheduledDeparture ? dayjs(a.scheduledDeparture).valueOf() : 0;
      const bTs = b.scheduledDeparture ? dayjs(b.scheduledDeparture).valueOf() : 0;
      return aTs - bTs;
    });
    return (
      <Table
        size="small"
        rowKey="tripId"
        columns={tripColumns}
        dataSource={orderedTrips}
        pagination={false}
      />
    );
  };

  return (
    <div className="flex flex-col h-full">
      {!disclaimerDismissed && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setDisclaimerDismissed(true)}
          message={t('otp_estimate_disclaimer')}
          className="mx-2 mb-2"
        />
      )}
      <div className="flex flex-wrap items-center gap-4 px-2 pb-2">
        <DatePicker
          value={selectedDate}
          onChange={(d) => setSelectedDate(d || dayjs())}
          allowClear={false}
          format="MMM DD, YYYY"
          disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
        />
        {isToday && (
          <Button
            onClick={() => setRefreshTick((n) => n + 1)}
            loading={loadingHistorical}
            icon={<FontAwesomeIcon icon={faSyncAlt} />}
          >
            {t('refresh')}
          </Button>
        )}
      </div>

      <div
        className="grid gap-3 px-2 pb-2"
        style={{ gridTemplateColumns: '280px minmax(0, 1fr)', height: 600 }}
      >
        {/* LEFT column: headline stats + per-route bar chart */}
        <div className="flex flex-col gap-3 overflow-hidden">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white rounded shadow-sm p-2 text-center">
              <div className="text-xs text-gray-500">{t('on_time')}</div>
              <div className="text-2xl font-bold" style={{ color: STATUS_COLORS[Statuses.OnTime] }}>
                {overallStats.onTimePercent}%
              </div>
              <div className="text-xs text-gray-400">{thousand(overallStats.onTimeCount)}</div>
            </div>
            <div className="bg-white rounded shadow-sm p-2 text-center">
              <div className="text-xs text-gray-500">{t('early')}</div>
              <div className="text-2xl font-bold" style={{ color: STATUS_COLORS[Statuses.Early] }}>
                {overallStats.earlyPercent}%
              </div>
              <div className="text-xs text-gray-400">{thousand(overallStats.earlyCount)}</div>
            </div>
            <div className="bg-white rounded shadow-sm p-2 text-center">
              <div className="text-xs text-gray-500">{t('delayed')}</div>
              <div className="text-2xl font-bold" style={{ color: STATUS_COLORS[Statuses.Delayed] }}>
                {overallStats.latePercent}%
              </div>
              <div className="text-xs text-gray-400">{thousand(overallStats.lateCount)}</div>
            </div>
          </div>
          <div className="bg-white rounded shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="text-sm font-semibold p-2 border-b">{t('routes')}</div>
            <div className="flex-1 overflow-y-auto p-2">
              {tableData.length === 0 ? (
                <div className="text-xs text-gray-400 text-center py-4">{t('no_data')}</div>
              ) : (
                tableData.map((row) => {
                  const total = row.totalTrips || 1;
                  const onTimePct = (row.onTimeCount / total) * 100;
                  const latePct = (row.delayedCount / total) * 100;
                  const earlyPct = (row.earlyCount / total) * 100;
                  const isSelected = row.routeId === selectedRouteId;
                  return (
                    <button
                      type="button"
                      key={row.routeId}
                      onClick={() => setSelectedRouteId(row.routeId)}
                      className={`flex items-center gap-2 my-1 w-full text-left px-1 py-0.5 rounded hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <div className="w-12 text-xs truncate" title={row.routeName}>{row.routeName}</div>
                      <div className="flex-1 flex h-3 rounded overflow-hidden bg-gray-100">
                        <div style={{ width: `${onTimePct}%`, background: STATUS_COLORS[Statuses.OnTime] }} />
                        <div style={{ width: `${earlyPct}%`, background: STATUS_COLORS[Statuses.Early] }} />
                        <div style={{ width: `${latePct}%`, background: STATUS_COLORS[Statuses.Delayed] }} />
                      </div>
                      <div className="text-xs w-10 text-right">{row.otpPercentage}%</div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* RIGHT column: route-analysis table — internal scroll. */}
        <div className="bg-white rounded shadow-sm h-full flex flex-col overflow-hidden">
          {loadingHistorical ? (
            <div className="flex items-center justify-center h-full"><Spin /></div>
          ) : tableData.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Empty description={t('no_data')} />
            </div>
          ) : (
            <Table
              size="small"
              rowKey="key"
              columns={tableColumns}
              dataSource={tableData}
              expandable={{ expandedRowRender }}
              rowClassName={(row) => (row.routeId === selectedRouteId ? 'bg-blue-50' : '')}
              scroll={{ y: 480 }}
              pagination={{ pageSize: 50, hideOnSinglePage: true }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AmaDashboardOtp;
